<script setup lang="ts">
import "@cisco-u/reicon-lock/cu-reicon-lock.js";

import { debounce, isString } from "lodash-es";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import AnchorTargetSelfCallback from "@/components/AnchorTargetSelfCallback.vue";
import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { ESSENTIALS, FREE } from "@/consts/bundles";
import { getCoursePaths } from "@/services/catalogService/courses";
import { removeProgress } from "@/services/middlewareService/learnerProgress";
import { EventBus } from "@/store/eventBus";
import { urlContentTypes } from "@/utils/contentRemovable";
import isUnauth from "@/utils/isUnauth";
import {
  launchSyndicateContent,
  shouldUseSyndicateLaunchFlow,
} from "@/utils/launchSyndicateContent";
import linkBackFormat from "@/utils/linkBackFormat";
import { toggleMyListItem } from "@/utils/myListActions";
import telemetry from "@/utils/telemetry";
import { getCleanXylemeCourseUrl } from "@/utils/validateUrl";

defineOptions({
  name: "CertCard",
});

const props = withDefaults(
  defineProps<{
    result: any;
    index?: number;
    status?: any;
    forCarousel?: boolean;
    cardView?: string;
    cardStyle?: string;
    priceView?: string;
    isRemovable?: boolean;
    topicView?: boolean;
    isAdminContent?: boolean;
    isStoreContent?: boolean;
    showDescription?: boolean;
  }>(),
  {
    index: 0,
    status: null,
    forCarousel: false,
    cardView: "Grid",
    cardStyle: "Grid",
    priceView: "USD",
    isRemovable: false,
    topicView: false,
    isAdminContent: false,
    isStoreContent: false,
    showDescription: true,
  }
);

const store = useStore();
const router = useRouter();
const route = useRoute();

const click = ref(false);
const paths = ref<any>(null);
const openModal = ref(false);
const isLaunching = ref(false);

const inMyList = computed(() => store.getters["user/inMyList"]);
const freeId = computed(() => store.getters["content/freeId"]);
const userStatus = computed(() => store.getters["user/status"]);

const freeContent = computed(() => {
  return props.result?.content_bundle?.includes(freeId.value);
});

const subLink = computed(() => {
  return router.resolve({
    name: "subscription",
  }).href;
});

const showPremium = computed(() => {
  return !props.status?.userEntitlements?.includes(props.result?.guid);
});

const hasParent = computed(() => {
  return props.result?.has_parent === true;
});

const removeFromMyProgress = (content: any) => {
  store.commit("user/removeFromMyProgress", content);
};

const removeContentFromMyList = (payload: any) => {
  return store.dispatch("user/removeContentFromMyList", payload);
};

const addContentToMyList = (payload: any) => {
  return store.dispatch("user/addContentToMyList", payload);
};

const fetchProgress = () => {
  return store.dispatch("user/fetchProgress");
};

const bookmarkMe = (content: any) => {
  toggleMyListItem(content, content.type, {
    inMyList: inMyList.value,
    addContentToMyList,
    removeContentFromMyList,
    route,
  });
};

const debouncedBookmarkMe = debounce((content: any) => {
  bookmarkMe(content);
}, 500);

const whereTo = async () => {
  getCoursePaths_(props.result.id);

  // For unauth users lacking entitlement, always show lock (with free/premium message)
  if (isUnauth() && showPremium.value) {
    click.value = true;
    return;
  }

  // For auth users with syndicate content, proceed with syndicate flow (don't lock)
  if (
    !isUnauth() &&
    getCleanXylemeCourseUrl(props.result) &&
    (await shouldUseSyndicateLaunchFlow(props.result))
  ) {
    click.value = false;
    sendTelemetry();
    return;
  }

  if (showPremium.value && hasParent.value) {
    click.value = true;
  } else {
    click.value = false;
    sendTelemetry();
  }
};

const hideClicked = () => {
  if (click.value && showPremium.value) {
    click.value = false;
  }
  if (click.value && hasParent.value) {
    click.value = false;
  }
};

const resourceUrl = () => {
  // Return empty string for unauthenticated users with parent content
  if (isUnauth() && hasParent.value) {
    return "";
  }

  // Get initial URL from catalog data or result
  let url = props.result.catalog_data?.url || props.result.url;
  const courseId = props.result.friendly_url ?? props.result.id;

  // Generate course URL for without parent, or invalid URLs
  const shouldGenerateCourseUrl =
    !hasParent.value || !url || url.length < 5 || url.includes("undefined");

  if (shouldGenerateCourseUrl && courseId) {
    url = router.resolve({
      name: "Course",
      params: { courseId },
    }).href;
  }

  // Return early for courses without parent
  if (!hasParent.value) {
    return url || "";
  }

  // Return empty string if no valid URL
  if (!url) {
    return "";
  }

  // Format URL with title for link back functionality
  const title =
    ((route?.meta?.title as string) ?? "") +
    ((route?.params?.type as string) ?? "");

  return linkBackFormat(url, title);
};

const openCartModal = () => {
  EventBus.emit("openCartModal", {
    data: paths.value,
    courseCard: true,
  });
};

const getCoursePaths_ = async (id: number) => {
  if (!isUnauth()) {
    await getCoursePaths(id)
      .then((data) => {
        paths.value = data?.paths;
      })
      .catch(() => {
        router.push({ name: "error" });
      });
  }
};

const createTelemetryObject = () => {
  const telemetryObject = {
    action: "launch-contentType",
    url: click.value ? subLink.value : props.result.url,
    title: props.result.title,
    id: props.result.id,
    page: route.fullPath,
    type: props.result.type,
    relevance: props.result.relevance,
    sortOrder: props.index,
    accessObject: {} as any,
    assignedContent: undefined as any,
  };

  if (showPremium.value) {
    setPremiumAccessInfo(telemetryObject.accessObject);
  }

  if (props.result.assigned_content) {
    telemetryObject.assignedContent = props.result.assigned_content?.isAssigned;
    telemetryObject.sortOrder = props.result.assigned_content?.index;
  }

  return telemetryObject;
};

const setPremiumAccessInfo = (accessObject: any) => {
  if (
    props.status?.bundles?.entitlements?.some(
      (entitlement: any) => entitlement.content_bundle_name === ESSENTIALS
    )
  ) {
    accessObject.contentAccessLevel = ESSENTIALS;
  } else {
    accessObject.contentAccessLevel = FREE;
  }

  accessObject.type = "contentEntitlementAccess";
  accessObject.status = "locked";
};

const handleXylemeSyndicateContent = () => {
  if (window.env.ENABLE_SYNDICATE_ENTITLEMENT_INTEGRATION) {
    if (isLaunching.value) return;
    isLaunching.value = true;
    launchSyndicateContent(
      userStatus.value,
      getCleanXylemeCourseUrl(props.result)
    )
      .then(() => {
        isLaunching.value = false;
      })
      .catch((err) => {
        EventBus.emit("showBanner", {
          type: "ERROR",
          message: err,
        });
        isLaunching.value = false;
      });
  } else {
    window.open(getCleanXylemeCourseUrl(props.result), "_blank", "noopener");
  }
};

const handlePremiumAccess = () => {
  router.push({ name: "subscription" });
};

const handleSpecialPublisher = () => {
  window.location.href = resourceUrl();
};

const handleDefaultAction = () => {
  window.open(resourceUrl(), "_blank", "noopener");
};

const handleTelemetryResponse = async () => {
  if (showPremium.value && hasParent.value) {
    handlePremiumAccess();
    return;
  }

  if (isUnauth()) {
    window.location.href = resourceUrl();
    return;
  }

  if (props.result?.url && (await shouldUseSyndicateLaunchFlow(props.result))) {
    handleXylemeSyndicateContent();
    return;
  }

  if (!hasParent.value) {
    router.push({
      name: "Course",
      params: {
        courseId: props.result.friendly_url
          ? props.result.friendly_url
          : props.result.id,
      },
    });
    return;
  }

  if (props.result.publisher_name === "Cisco") {
    handleSpecialPublisher();
  } else {
    handleDefaultAction();
  }
};

const sendTelemetry = async () => {
  const telemetryObject = createTelemetryObject();
  const telemetryResponse = telemetry.external(telemetryObject, isUnauth());

  telemetryResponse
    .then(async () => {
      await handleTelemetryResponse();
    })
    .catch(() => {
      // do nothing
    });
};

const returnUrl = (url: string) => {
  if (!isString(url)) return "";
  if (window.location.href.includes("for-you")) {
    return linkBackFormat(url, "for you page");
  }
  if (window.location.href.includes("explore")) {
    return linkBackFormat(url, "explore page");
  }
  return linkBackFormat(url, "search results");
};

const sendRmTelemetry = async (content: any) => {
  const contentId = content.content_id || content.id || "";
  return await telemetry.remove_from_myLearning(
    window.location.origin + route.fullPath,
    `/${urlContentTypes(content.type)}/${contentId}`,
    content.title || content.name || "",
    content.type,
    contentId,
    isUnauth()
  );
};

const removeFromMyProgressList = async (content: any) => {
  removeProgress(content.guid, content.type)
    .then(() => {
      sendRmTelemetry(content);
      removeFromMyProgress(content);
      fetchProgress();
    })
    .catch(() => {
      // do nothing
    });
};

onMounted(() => {
  EventBus.on("modalOpen", () => {
    openModal.value = true;
  });
});

defineExpose({
  click,
  paths,
  openModal,
  isLaunching,
  freeContent,
  subLink,
  showPremium,
  hasParent,
  bookmarkMe,
  debouncedBookmarkMe,
  whereTo,
  hideClicked,
  resourceUrl,
  openCartModal,
  sendTelemetry,
  createTelemetryObject,
  setPremiumAccessInfo,
  handleTelemetryResponse,
  handleXylemeSyndicateContent,
  handlePremiumAccess,
  handleSpecialPublisher,
  handleDefaultAction,
  returnUrl,
  sendRmTelemetry,
  removeFromMyProgressList,
  addContentToMyList,
  removeContentFromMyList,
  removeFromMyProgress,
  fetchProgress,
});
</script>

<template>
  <div
    data-cy="course-card"
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView == 'Grid' && !topicView,
        'w-full': props.cardView != 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
    v-show="result"
  >
    <div
      v-if="isLaunching"
      class="absolute inset-0 z-10 bg-black-lightest opacity-50 flex items-center justify-center pointer-events-none"
    >
      <div class="spinner"></div>
    </div>
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :link="resourceUrl()"
      :cy="'course-card-link'"
      :show-description="props.showDescription"
      @navigate="whereTo"
      @bookmark="debouncedBookmarkMe(result)"
      @remove-progress="removeFromMyProgressList(result)"
      :is-removable="isRemovable"
      :is-admin-content="isAdminContent"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'course-product-card-link'"
      :link="resourceUrl()"
      :show-description="props.showDescription"
      @navigate="whereTo"
      @bookmark="debouncedBookmarkMe(result)"
      @remove-progress="removeFromMyProgressList(result)"
      :is-removable="isRemovable"
      :is-admin-content="isAdminContent"
    />
    <div
      :class="[
        'mt-0',
        {
          hidden: !showPremium || !click,
          'mask_it absolute left-0 top-0 mt-0 h-full w-full rounded-[0.625rem] bg-[#000000]/80':
            (showPremium && click) || (hasParent && click),
        },
      ]"
    >
      <cu-reicon-lock
        type="filled-keyhole"
        dark="true"
        size="xl"
        :class="[
          'flex justify-center',
          {
            'mt-12': props.cardView == 'Grid',
            'mt-2': props.cardView != 'Grid',
          },
        ]"
      />
      <div
        class="mx-2 text-center text-13 text-white-lightest"
        v-click-outside="{
          handler: hideClicked,
          events: ['dblclick', 'click'],
          capture: true,
        }"
      >
        <p v-if="isUnauth() && !freeContent">
          This content is included in Cisco U. subscription plans.<br /><router-link
            class="underline"
            :to="{ name: 'subscription' }"
            >View your options to subscribe.</router-link
          >
        </p>
        <div v-else-if="isUnauth() && freeContent">
          <a href="/login" class="text-white-lightest underline">Sign up</a>
          to explore this free {{ result.type }}.
        </div>
        <div v-else-if="hasParent">
          <p>Courses are not sold individually. To access this course,</p>
          <AnchorTargetSelfCallback
            :url="subLink"
            class="underline"
            @clicked="openCartModal()"
            >choose a Learning Path.</AnchorTargetSelfCallback
          >
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.spinner {
  top: 0;
}
</style>
